import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

/* -------------------- Helpers -------------------- */
const FREQUENCIES = ["daily", "weekly", "monthly", "quarterly", "yearly"];
const METRIC_TYPES = ["number", "currency", "percent", "text"];

function currentPeriod(freq) {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  if (freq === "yearly") return `${y}`;
  if (freq === "quarterly") return `${y}-Q${Math.floor(d.getMonth() / 3) + 1}`;
  if (freq === "weekly") {
    // ISO week (approximate)
    const t = new Date(Date.UTC(y, d.getMonth(), d.getDate()));
    t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
    const ys = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    const w = Math.ceil(((t - ys) / 86400000 + 1) / 7);
    return `${t.getUTCFullYear()}-W${String(w).padStart(2, "0")}`;
  }
  if (freq === "daily") return `${y}-${m}-${String(d.getDate()).padStart(2, "0")}`;
  return `${y}-${m}`; // monthly
}

function pickName(g, lang) {
  return (lang === "fr" && g.name_fr) ? g.name_fr : g.name_en;
}

function pickLabel(def, lang) {
  return (lang === "fr" && def.label_fr) ? def.label_fr : def.label_en;
}

/* -------------------- List page -------------------- */
export default function MetricGroups() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mine"); // 'mine' | 'assigned' | 'shared'
  const [mine, setMine] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [m, a, s] = await Promise.all([
        api.get("/metric-groups?scope=mine").then((r) => r.data).catch(() => []),
        api.get("/metric-groups?scope=assigned").then((r) => r.data).catch(() => []),
        api.get("/metric-groups?scope=shared").then((r) => r.data).catch(() => []),
      ]);
      setMine(m); setAssigned(a); setShared(s);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canCreate = !!user;

  const remove = async (gid) => {
    if (!window.confirm(t("metricGroups.confirmDelete"))) return;
    try {
      await api.delete(`/metric-groups/${gid}`);
      refresh();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="tb-app" data-testid="metric-groups-page">
      <div className="tb-page-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="tb-h1">{t("metricGroups.title")}</h1>
          <p className="tb-muted" style={{ marginTop: 4 }}>{t("metricGroups.subtitle")}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/app/metrics" className="tb-ghost-btn" data-testid="back-to-metrics">{t("metricGroups.backToMetrics")}</Link>
          {canCreate && (
            <button className="tb-primary-btn" onClick={() => navigate("/app/metric-groups/new")} data-testid="new-group-btn">
              + {t("metricGroups.newGroup")}
            </button>
          )}
        </div>
      </div>

      <div className="tb-admin-tabs" data-testid="mg-tabs" style={{ marginTop: 12 }}>
        <button className={tab === "mine" ? "on" : ""} onClick={() => setTab("mine")} data-testid="mg-tab-mine">{t("metricGroups.tabs.mine")} ({mine.length})</button>
        <button className={tab === "assigned" ? "on" : ""} onClick={() => setTab("assigned")} data-testid="mg-tab-assigned">{t("metricGroups.tabs.assigned")} ({assigned.length})</button>
        <button className={tab === "shared" ? "on" : ""} onClick={() => setTab("shared")} data-testid="mg-tab-shared">{t("metricGroups.tabs.shared")} ({shared.length})</button>
      </div>

      {err && <p style={{ color: "#ff7a7a", marginTop: 12 }}>{err}</p>}

      {loading ? (
        <p className="tb-muted" style={{ marginTop: 16 }}>{t("common.loading")}</p>
      ) : (
        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
          {tab === "mine" && mine.length === 0 && (
            <EmptyState
              label={t("metricGroups.emptyMine")}
              cta={t("metricGroups.newGroup")}
              onCta={() => navigate("/app/metric-groups/new")}
            />
          )}
          {tab === "mine" && mine.map((g) => (
            <GroupCard
              key={g.id} group={g} lang={lang} t={t}
              actions={
                <>
                  <Link to={`/app/metric-groups/${g.id}`} className="tb-ghost-btn" data-testid={`edit-${g.id}`}>{t("metricGroups.edit")}</Link>
                  <Link to={`/app/metric-groups/${g.id}/assign`} className="tb-ghost-btn" data-testid={`assign-${g.id}`}>{t("metricGroups.assign")}</Link>
                  <button className="tb-ghost-btn" style={{ color: "#ff7a7a", borderColor: "#ff7a7a55" }} onClick={() => remove(g.id)} data-testid={`del-${g.id}`}>{t("common.delete")}</button>
                </>
              }
            />
          ))}

          {tab === "assigned" && assigned.length === 0 && (
            <EmptyState label={t("metricGroups.emptyAssigned")} />
          )}
          {tab === "assigned" && assigned.map((g) => (
            <GroupCard
              key={g.id} group={g} lang={lang} t={t}
              badge={`${t("metricGroups.assignedBy")} ${(g.assigned_by?.name || g.assigned_by?.email || "—")}`}
              actions={<Link to={`/app/metric-groups/${g.id}/track`} className="tb-primary-btn" data-testid={`track-${g.id}`}>{t("metricGroups.track")}</Link>}
            />
          ))}

          {tab === "shared" && shared.length === 0 && (
            <EmptyState label={t("metricGroups.emptyShared")} />
          )}
          {tab === "shared" && shared.map((g) => (
            <GroupCard
              key={g.id} group={g} lang={lang} t={t}
              badge={`${t("metricGroups.targetUser")}: ${g.target_user_id?.slice(0, 8) || "—"}`}
              actions={<Link to={`/app/metric-groups/${g.id}/view?u=${g.target_user_id}`} className="tb-ghost-btn" data-testid={`view-${g.id}`}>{t("metricGroups.viewValues")}</Link>}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Sub-components -------------------- */
function EmptyState({ label, cta, onCta }) {
  return (
    <div className="tb-card" style={{ textAlign: "center", padding: 32 }} data-testid="empty-state">
      <p className="tb-muted">{label}</p>
      {cta && <button className="tb-primary-btn" onClick={onCta} style={{ marginTop: 8 }} data-testid="empty-cta">{cta}</button>}
    </div>
  );
}

function GroupCard({ group, lang, t, badge, actions }) {
  return (
    <div className="tb-card" data-testid={`group-card-${group.id}`}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h3 style={{ margin: 0 }}>{pickName(group, lang)}</h3>
          {group.description && <p className="tb-muted" style={{ marginTop: 6, marginBottom: 0 }}>{group.description}</p>}
          <p className="tb-muted" style={{ marginTop: 8, fontSize: 13 }}>
            {t("metricGroups.itemsCount", { count: group.items?.length || 0 })}
            {badge && <> · {badge}</>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>
      </div>
    </div>
  );
}

/* -------------------- Builder (create + edit) -------------------- */
export function MetricGroupBuilder() {
  const { gid } = useParams();
  const isEdit = !!gid;
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name_en: "", name_fr: "", description: "" });
  const [items, setItems] = useState([]); // { metric_def_id, frequency, required, order, help_text, definition? }
  const [catalog, setCatalog] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showNewDef, setShowNewDef] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/metric-definitions").then((r) => setCatalog(r.data)).catch(() => {});
    if (gid) {
      api.get(`/metric-groups/${gid}`).then((r) => {
        const g = r.data;
        setForm({ name_en: g.name_en, name_fr: g.name_fr || "", description: g.description || "" });
        setItems((g.resolved_items || []).map((it, i) => ({ ...it, order: it.order ?? i })));
      }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    }
  }, [gid]);

  const save = async () => {
    if (!form.name_en.trim()) { setErr(t("metricGroups.builder.nameRequired")); return; }
    if (items.length === 0) { setErr(t("metricGroups.builder.atLeastOne")); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        name_en: form.name_en, name_fr: form.name_fr || form.name_en,
        description: form.description,
        items: items.map((it, i) => ({
          metric_def_id: it.metric_def_id || it.definition?.id,
          order: i, frequency: it.frequency, required: !!it.required, help_text: it.help_text || "",
        })),
      };
      if (isEdit) await api.put(`/metric-groups/${gid}`, payload);
      else await api.post("/metric-groups", payload);
      navigate("/app/metric-groups");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const addFromCatalog = (def) => {
    if (items.find((x) => (x.metric_def_id || x.definition?.id) === def.id)) return;
    setItems([...items, { metric_def_id: def.id, definition: def, frequency: "monthly", required: false, order: items.length, help_text: "" }]);
    setShowAdd(false);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const createDef = async (defForm) => {
    try {
      const r = await api.post("/metric-definitions", defForm);
      setCatalog([r.data, ...catalog]);
      addFromCatalog(r.data);
      setShowNewDef(false);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="tb-app" data-testid="metric-group-builder">
      <div className="tb-page-header">
        <h1 className="tb-h1">{isEdit ? t("metricGroups.builder.editTitle") : t("metricGroups.builder.createTitle")}</h1>
        <p className="tb-muted">{t("metricGroups.builder.subtitle")}</p>
      </div>

      <div className="tb-card" style={{ marginTop: 16 }}>
        <label className="tb-label">{t("metricGroups.builder.nameEn")}</label>
        <input className="tb-input" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="name-en" />

        <label className="tb-label" style={{ marginTop: 10 }}>{t("metricGroups.builder.nameFr")}</label>
        <input className="tb-input" value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} data-testid="name-fr" />

        <label className="tb-label" style={{ marginTop: 10 }}>{t("metricGroups.builder.description")}</label>
        <textarea className="tb-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="description" />
      </div>

      <div className="tb-card" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{t("metricGroups.builder.metrics")} ({items.length})</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="tb-ghost-btn" onClick={() => setShowAdd(true)} data-testid="add-from-catalog">+ {t("metricGroups.builder.addExisting")}</button>
            <button className="tb-ghost-btn" onClick={() => setShowNewDef(true)} data-testid="create-new-def">+ {t("metricGroups.builder.createNew")}</button>
          </div>
        </div>

        {items.length === 0 && <p className="tb-muted">{t("metricGroups.builder.noMetrics")}</p>}
        {items.map((it, idx) => (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 140px 80px", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)" }} data-testid={`item-row-${idx}`}>
            <div>
              <strong>{pickLabel(it.definition || {}, lang) || it.metric_def_id}</strong>
              {it.definition?.unit && <span className="tb-muted"> · {it.definition.unit}</span>}
              {it.definition?.type && <span className="tb-muted" style={{ fontSize: 12 }}> · {it.definition.type}</span>}
            </div>
            <select className="tb-input" value={it.frequency} onChange={(e) => updateItem(idx, { frequency: e.target.value })} data-testid={`freq-${idx}`}>
              {FREQUENCIES.map((f) => <option key={f} value={f}>{t(`metricGroups.frequency.${f}`)}</option>)}
            </select>
            <button className="tb-ghost-btn" onClick={() => removeItem(idx)} style={{ color: "#ff7a7a", borderColor: "#ff7a7a55" }} data-testid={`remove-${idx}`}>×</button>
          </div>
        ))}
      </div>

      {err && <p style={{ color: "#ff7a7a", marginTop: 12 }}>{err}</p>}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="tb-primary-btn" onClick={save} disabled={busy} data-testid="save-group">{busy ? t("common.saving") : t("common.save")}</button>
        <Link to="/app/metric-groups" className="tb-ghost-btn">{t("common.cancel")}</Link>
      </div>

      {showAdd && (
        <CatalogPicker
          catalog={catalog.filter((d) => !items.find((x) => (x.metric_def_id || x.definition?.id) === d.id))}
          lang={lang} t={t}
          onPick={addFromCatalog}
          onClose={() => setShowAdd(false)}
        />
      )}
      {showNewDef && (
        <NewDefinitionModal lang={lang} t={t} onCreate={createDef} onClose={() => setShowNewDef(false)} />
      )}
    </div>
  );
}

function CatalogPicker({ catalog, lang, t, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return catalog.filter((d) => !s || d.label_en.toLowerCase().includes(s) || (d.label_fr || "").toLowerCase().includes(s) || d.key.includes(s));
  }, [catalog, q]);

  return (
    <div className="tb-modal-overlay" onClick={onClose} data-testid="catalog-picker">
      <div className="tb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h3 style={{ marginTop: 0 }}>{t("metricGroups.picker.title")}</h3>
        <input className="tb-input" placeholder={t("metricGroups.picker.search")} value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="picker-search" />
        <div style={{ maxHeight: 360, overflowY: "auto", marginTop: 12 }}>
          {filtered.length === 0 && <p className="tb-muted">{t("metricGroups.picker.empty")}</p>}
          {filtered.map((d) => (
            <button key={d.id} className="tb-ghost-btn" onClick={() => onPick(d)} style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 6 }} data-testid={`pick-${d.id}`}>
              <strong>{pickLabel(d, lang)}</strong>
              {d.unit && <span className="tb-muted"> · {d.unit}</span>}
              <span className="tb-muted" style={{ fontSize: 12 }}> · {d.type}</span>
              {d.description && <div className="tb-muted" style={{ fontSize: 12, marginTop: 4 }}>{d.description}</div>}
            </button>
          ))}
        </div>
        <button className="tb-ghost-btn" onClick={onClose} style={{ marginTop: 12 }}>{t("common.close")}</button>
      </div>
    </div>
  );
}

function NewDefinitionModal({ lang, t, onCreate, onClose }) {
  const [form, setForm] = useState({ key: "", label_en: "", label_fr: "", unit: "", type: "number", description: "" });
  return (
    <div className="tb-modal-overlay" onClick={onClose} data-testid="new-def-modal">
      <div className="tb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h3 style={{ marginTop: 0 }}>{t("metricGroups.newDef.title")}</h3>
        <label className="tb-label">{t("metricGroups.newDef.key")}</label>
        <input className="tb-input" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="mrr" data-testid="def-key" />

        <label className="tb-label" style={{ marginTop: 8 }}>{t("metricGroups.newDef.labelEn")}</label>
        <input className="tb-input" value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })} placeholder="Monthly Recurring Revenue" data-testid="def-label-en" />

        <label className="tb-label" style={{ marginTop: 8 }}>{t("metricGroups.newDef.labelFr")}</label>
        <input className="tb-input" value={form.label_fr} onChange={(e) => setForm({ ...form, label_fr: e.target.value })} placeholder="Revenu mensuel récurrent" data-testid="def-label-fr" />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
          <div>
            <label className="tb-label">{t("metricGroups.newDef.unit")}</label>
            <input className="tb-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="$, %, users…" data-testid="def-unit" />
          </div>
          <div>
            <label className="tb-label">{t("metricGroups.newDef.type")}</label>
            <select className="tb-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="def-type">
              {METRIC_TYPES.map((tp) => <option key={tp} value={tp}>{t(`metricGroups.types.${tp}`)}</option>)}
            </select>
          </div>
        </div>

        <label className="tb-label" style={{ marginTop: 8 }}>{t("metricGroups.newDef.description")}</label>
        <textarea className="tb-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="def-description" />

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="tb-primary-btn" onClick={() => onCreate(form)} disabled={!form.key.trim() || !form.label_en.trim()} data-testid="def-save">{t("common.create")}</button>
          <button className="tb-ghost-btn" onClick={onClose}>{t("common.cancel")}</button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Track (founder records values) -------------------- */
export function MetricGroupTrack() {
  const { gid } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [values, setValues] = useState({}); // metric_def_id → { period: value }
  const [periodMap, setPeriodMap] = useState({}); // metric_def_id → currently-selected period
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState("");
  const [saveMsg, setSaveMsg] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const g = (await api.get(`/metric-groups/${gid}`)).data;
        setGroup(g);
        const init = {};
        (g.resolved_items || []).forEach((it) => { init[it.metric_def_id] = currentPeriod(it.frequency); });
        setPeriodMap(init);
        const v = (await api.get(`/metric-groups/${gid}/values`)).data;
        // build lookup: metric_def_id → period → value
        const lookup = {};
        for (const [mid, rows] of Object.entries(v.values_by_metric || {})) {
          lookup[mid] = {};
          rows.forEach((r) => { lookup[mid][r.period] = r.value; });
        }
        setValues(lookup);
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    };
    load();
  }, [gid]);

  const save = async (metric_def_id, period, value) => {
    setBusy({ ...busy, [metric_def_id]: true });
    try {
      await api.put(`/metric-groups/${gid}/values`, { metric_def_id, period, value });
      setValues((cur) => ({ ...cur, [metric_def_id]: { ...(cur[metric_def_id] || {}), [period]: value } }));
      setSaveMsg({ ...saveMsg, [metric_def_id]: t("metricGroups.track.saved") });
      setTimeout(() => setSaveMsg((s) => ({ ...s, [metric_def_id]: "" })), 1800);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy({ ...busy, [metric_def_id]: false }); }
  };

  if (!group) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div className="tb-app" data-testid="metric-group-track">
      <div className="tb-page-header">
        <h1 className="tb-h1">{pickName(group, lang)}</h1>
        {group.description && <p className="tb-muted">{group.description}</p>}
        <Link to="/app/metric-groups" className="tb-ghost-btn" style={{ marginTop: 8 }}>← {t("metricGroups.backToList")}</Link>
      </div>

      {err && <p style={{ color: "#ff7a7a" }}>{err}</p>}

      <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
        {(group.resolved_items || []).map((it) => {
          const period = periodMap[it.metric_def_id] || currentPeriod(it.frequency);
          const current = values[it.metric_def_id]?.[period] || "";
          return (
            <div key={it.metric_def_id} className="tb-card" data-testid={`track-row-${it.metric_def_id}`}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong>{pickLabel(it.definition, lang)}</strong>
                  {it.definition.unit && <span className="tb-muted"> · {it.definition.unit}</span>}
                  <div className="tb-muted" style={{ fontSize: 12, marginTop: 4 }}>{t(`metricGroups.frequency.${it.frequency}`)}</div>
                </div>
                <input className="tb-input" style={{ width: 140 }}
                  value={period}
                  onChange={(e) => setPeriodMap({ ...periodMap, [it.metric_def_id]: e.target.value })}
                  placeholder="YYYY-MM" data-testid={`period-${it.metric_def_id}`}
                />
                <input className="tb-input" style={{ width: 160 }}
                  type={it.definition.type === "text" ? "text" : "number"}
                  value={current}
                  onChange={(e) => setValues((cur) => ({ ...cur, [it.metric_def_id]: { ...(cur[it.metric_def_id] || {}), [period]: e.target.value } }))}
                  placeholder={t("metricGroups.track.value")} data-testid={`value-${it.metric_def_id}`}
                />
                <button className="tb-primary-btn" disabled={busy[it.metric_def_id]}
                  onClick={() => save(it.metric_def_id, period, current)}
                  data-testid={`save-${it.metric_def_id}`}>
                  {busy[it.metric_def_id] ? "…" : t("common.save")}
                </button>
                {saveMsg[it.metric_def_id] && <span style={{ color: "#6ee7b7", fontSize: 12 }}>{saveMsg[it.metric_def_id]}</span>}
              </div>
              {Object.keys(values[it.metric_def_id] || {}).length > 1 && (
                <div className="tb-muted" style={{ fontSize: 12, marginTop: 10 }}>
                  {t("metricGroups.track.history")}: {Object.entries(values[it.metric_def_id] || {}).sort((a, b) => (a[0] < b[0] ? 1 : -1)).slice(0, 5).map(([p, v]) => `${p}=${v}`).join(" · ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------- View (assigner reviews founder values) -------------------- */
export function MetricGroupView() {
  const { gid } = useParams();
  const { t, lang } = useI18n();
  const [group, setGroup] = useState(null);
  const [values, setValues] = useState(null);
  const [err, setErr] = useState("");
  const targetUid = new URLSearchParams(window.location.search).get("u");

  useEffect(() => {
    const load = async () => {
      try {
        const g = (await api.get(`/metric-groups/${gid}`)).data;
        setGroup(g);
        const v = (await api.get(`/metric-groups/${gid}/values${targetUid ? `?target_user_id=${targetUid}` : ""}`)).data;
        setValues(v);
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    };
    load();
  }, [gid, targetUid]);

  if (!group || !values) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div className="tb-app" data-testid="metric-group-view">
      <div className="tb-page-header">
        <h1 className="tb-h1">{pickName(group, lang)}</h1>
        <p className="tb-muted">{t("metricGroups.view.subtitle")}</p>
        <Link to="/app/metric-groups" className="tb-ghost-btn" style={{ marginTop: 8 }}>← {t("metricGroups.backToList")}</Link>
      </div>

      {err && <p style={{ color: "#ff7a7a" }}>{err}</p>}

      <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
        {(group.resolved_items || []).map((it) => {
          const latest = values.latest_per_metric?.[it.metric_def_id];
          const history = values.values_by_metric?.[it.metric_def_id] || [];
          return (
            <div key={it.metric_def_id} className="tb-card" data-testid={`view-row-${it.metric_def_id}`}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "baseline" }}>
                <strong>{pickLabel(it.definition, lang)}</strong>
                {it.definition.unit && <span className="tb-muted">{it.definition.unit}</span>}
                <span className="tb-muted" style={{ fontSize: 12, marginLeft: "auto" }}>{t(`metricGroups.frequency.${it.frequency}`)}</span>
              </div>
              <div style={{ marginTop: 8 }}>
                {latest ? (
                  <>
                    <span style={{ fontSize: 22, fontWeight: 600 }}>{latest.value}</span>
                    <span className="tb-muted" style={{ marginLeft: 12, fontSize: 13 }}>
                      {t("metricGroups.view.lastUpdate")}: {latest.period} · {new Date(latest.recorded_at).toLocaleString()}
                    </span>
                  </>
                ) : (
                  <span className="tb-muted">{t("metricGroups.view.noData")}</span>
                )}
              </div>
              {history.length > 1 && (
                <div className="tb-muted" style={{ fontSize: 12, marginTop: 8 }}>
                  {t("metricGroups.view.history")}: {history.slice(0, 6).map((r) => `${r.period}=${r.value}`).join(" · ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------- Assign (owner picks a connection) -------------------- */
export function MetricGroupAssign() {
  const { gid } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [connections, setConnections] = useState([]);
  const [existing, setExisting] = useState([]); // assignment rows for this group
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/metric-groups/${gid}`).then((r) => setGroup(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get("/connections").then((r) => setConnections(r.data || [])).catch(() => setConnections([]));
    api.get("/metric-groups?scope=shared").then((r) => setExisting((r.data || []).filter((g) => g.id === gid))).catch(() => {});
  }, [gid]);

  const assignTo = async (uid) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/metric-groups/${gid}/assign`, { target_user_id: uid });
      const refreshed = await api.get("/metric-groups?scope=shared");
      setExisting((refreshed.data || []).filter((g) => g.id === gid));
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const unassign = async (aid) => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/metric-groups/${gid}/assignments/${aid}`);
      const refreshed = await api.get("/metric-groups?scope=shared");
      setExisting((refreshed.data || []).filter((g) => g.id === gid));
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!group) return <div className="tb-loading">{t("common.loading")}</div>;

  const alreadyAssignedIds = new Set(existing.map((e) => e.target_user_id));

  return (
    <div className="tb-app" data-testid="metric-group-assign">
      <div className="tb-page-header">
        <h1 className="tb-h1">{t("metricGroups.assignTitle")}: {pickName(group, lang)}</h1>
        <p className="tb-muted">{t("metricGroups.assignSubtitle")}</p>
        <Link to="/app/metric-groups" className="tb-ghost-btn" style={{ marginTop: 8 }}>← {t("metricGroups.backToList")}</Link>
      </div>

      {err && <p style={{ color: "#ff7a7a" }}>{err}</p>}

      {existing.length > 0 && (
        <div className="tb-card" style={{ marginTop: 16 }} data-testid="current-assignees">
          <h3 style={{ marginTop: 0 }}>{t("metricGroups.currentAssignees")} ({existing.length})</h3>
          {existing.map((e) => (
            <div key={e.assignment_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
              <span>{e.target_user_id?.slice(0, 12)}…</span>
              <button className="tb-ghost-btn" style={{ color: "#ff7a7a", borderColor: "#ff7a7a55" }} onClick={() => unassign(e.assignment_id)} disabled={busy} data-testid={`unassign-${e.assignment_id}`}>{t("metricGroups.unassign")}</button>
            </div>
          ))}
        </div>
      )}

      <div className="tb-card" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t("metricGroups.yourConnections")} ({connections.length})</h3>
        {connections.length === 0 && <p className="tb-muted">{t("metricGroups.noConnections")}</p>}
        {connections.map((c) => {
          const uid = c.other_user_id || c.user_id || c.id;
          const name = c.other_user_name || c.name || c.email;
          const role = c.other_user_role || c.role;
          const isAssigned = alreadyAssignedIds.has(uid);
          return (
            <div key={uid} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }} data-testid={`conn-${uid}`}>
              <div>
                <strong>{name}</strong>
                {role && <span className="tb-muted"> · {role}</span>}
              </div>
              {isAssigned ? (
                <span style={{ color: "#6ee7b7", fontSize: 13 }}>✓ {t("metricGroups.assigned")}</span>
              ) : (
                <button className="tb-primary-btn" onClick={() => assignTo(uid)} disabled={busy} data-testid={`assign-to-${uid}`}>{t("metricGroups.assign")}</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
